import React from "react";
import { Post, MinimalPost } from "../types/Post";
import { Row, Spacing } from "../helpers/layout";
import styled, { css } from "styled-components";
import { getImageUrl } from "../helpers/getImageUrl";
import NextLink from "next/link";
import { Colors } from "../branding";
import ArrowLeft from "../icons/ArrowLeft";
import ArrowRight from "../icons/ArrowRight";
import { useResponsive } from "../context/ResponsiveContext";
import { fontFamilies } from "../typography";

const H4 = styled.h4`
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const Label = styled.h3`
  margin-bottom: ${Spacing.s};
  font-family: ${fontFamilies.freight};
  font-size: 22px;
  font-style: italic;
  letter-spacing: 0.1em;
  font-weight: 400;
`;

function Arrow(props: { type: "previous" | "next" }) {
  const arrowStyles = css`
    position: absolute;
    top: 0;
    bottom: 0;
    height: 40px;
    margin: auto;

    svg {
      width: 30px;
      height: 40px;
    }
  `;

  if (props.type === "previous") {
    return (
      <div
        css={css`
          ${arrowStyles};
          left: ${Spacing.xxl};
        `}
      >
        <ArrowLeft />
      </div>
    );
  }

  if (props.type === "next") {
    return (
      <div
        css={css`
          ${arrowStyles};
          right: ${Spacing.xxl};
        `}
      >
        <ArrowRight />
      </div>
    );
  }

  return null;
}

function NoPost(props: { type: "previous" | "next" }) {
  const { isMobile } = useResponsive();
  const { type } = props;

  return (
    <div
      css={css`
        position: relative;
        width: 100%;
        height: ${isMobile ? `120px` : `214px`};
        display: flex;
        align-items: center;
        text-align: center;
        justify-content: center;
        background: ${Colors.very_light_grey};
      `}
    >
      <div
        css={css`
          opacity: 0.4;
        `}
      >
        {type === "next" ? (
          <>
            <Label>Next Post</Label>
            <H4>You are currently on the most recent post</H4>
          </>
        ) : (
          <>
            <Label>Previous Post</Label>
            <H4>You are currently on the oldest post</H4>
          </>
        )}
        <Arrow type={type} />
      </div>
    </div>
  );
}

function PaginationPost(props: {
  post: MinimalPost;
  type: "previous" | "next";
}) {
  const { post, type } = props;
  const { isMobile, isTablet } = useResponsive();

  return (
    <div
      css={css`
        position: relative;
        width: 100%;
        height: ${isMobile ? `120px` : `214px`};
        background: url(${getImageUrl(post.featured_image)});
        background-size: cover;
        background-position: 50%;
        color: ${Colors.white};

        &:hover {
          .inner {
            background: rgba(0, 0, 0, 0.8);
          }
        }

        a {
          text-decoration: none;
          color: inherit;
        }
      `}
    >
      <NextLink href="/blog/[slug]" as={`/blog/${post.slug}`}>
        <a href={`/blog/${post.slug}`}>
          <div
            className="inner"
            css={css`
              position: absolute;
              width: 100%;
              height: 100%;
              top: 0;
              left: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              background: rgba(0, 0, 0, 0.6);
              transition: background 400ms;
              text-align: center;
            `}
          >
            {!isMobile && !isTablet && (
              <div>
                <Label>{type === "next" ? "Next Post" : "Previous Post"}</Label>
                <H4>{post.name}</H4>
              </div>
            )}
            <Arrow type={type} />
          </div>
        </a>
      </NextLink>
    </div>
  );
}

export function PostPagination(props: { post: Post }) {
  const { post } = props;

  return (
    <nav
      css={css`
        padding-bottom: ${Spacing.m};
      `}
    >
      <Row
        css={css`
          width: 100%;
        `}
        justify="center"
      >
        <div
          css={css`
            width: 45%;
            flex: 1 0 45%;
          `}
        >
          {post.previous ? (
            <PaginationPost post={post.previous} type="previous" />
          ) : (
            <NoPost type="previous" />
          )}
        </div>
        <div
          css={css`
            width: 45%;
            flex: 1 0 45%;
          `}
        >
          {post.next ? (
            <PaginationPost post={post.next} type="next" />
          ) : (
            <NoPost type="next" />
          )}
        </div>
      </Row>
    </nav>
  );
}
